import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users, Plus, Search, Eye, Edit, Trash2, RefreshCw,
  X, GraduationCap, CheckCircle, Clock, Mail, User,
  Briefcase, Key, Building, Send, Loader2, Award,
  MessageSquare, ExternalLink, Copy, Power, FileText, RotateCcw
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JoinedPortalAdmin = () => {
  const { token } = useAuth();
  const [joinees, setJoinees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [feedback, setFeedback] = useState([]);
  const [activeTab, setActiveTab] = useState("joinees"); // joinees, email-template

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedJoinee, setSelectedJoinee] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    position: "",
    department: "",
    reporting_manager: "",
    notes: ""
  });

  // Credentials form
  const [credentialsForm, setCredentialsForm] = useState({
    crm_username: "",
    crm_password: "",
    crm_link: "",
    email_address: "",
    email_password: "",
    email_link: ""
  });

  // Email template state
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    subject: "",
    body: ""
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState({ subject: "", body: "" });

  const fetchData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [joineesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/joined/list`, authHeaders),
        axios.get(`${API}/admin/joined/stats`, authHeaders)
      ]);
      setJoinees(joineesRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Failed to load joinees:", err);
      toast.error("Failed to load training data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchEmailTemplate = useCallback(async () => {
    if (!token) return;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.get(`${API}/admin/joined/email-template`, authHeaders);
      setEmailTemplate(res.data);
      setTemplateForm({
        name: res.data.name || "",
        subject: res.data.subject || "",
        body: res.data.body || ""
      });
    } catch (err) {
      console.error("Failed to load email template:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === "email-template") {
      fetchEmailTemplate();
    }
  }, [activeTab, fetchEmailTemplate]);

  const filteredJoinees = joinees.filter(j => {
    const matchesSearch = j.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          j.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          j.position.toLowerCase().includes(searchTerm.toLowerCase());
    if (statusFilter === "all") return matchesSearch;
    if (statusFilter === "in_progress") return matchesSearch && j.completed_days?.length < 4;
    if (statusFilter === "completed") return matchesSearch && j.completed_days?.length >= 4;
    return matchesSearch;
  });

  const handleAdd = () => {
    setFormData({
      name: "",
      email: "",
      position: "",
      department: "",
      reporting_manager: "",
      notes: ""
    });
    setEditMode(false);
    setShowAddModal(true);
  };

  const handleEdit = (joinee) => {
    setFormData({
      name: joinee.name,
      email: joinee.email,
      position: joinee.position,
      department: joinee.department || "",
      reporting_manager: joinee.reporting_manager || "",
      notes: joinee.notes || ""
    });
    setSelectedJoinee(joinee);
    setEditMode(true);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    
    try {
      if (editMode) {
        await axios.put(`${API}/admin/joined/${selectedJoinee.id}`, formData, authHeaders);
        toast.success("Joinee updated successfully");
      } else {
        await axios.post(`${API}/admin/joined/add`, formData, authHeaders);
        toast.success("Joinee added and welcome email sent!");
      }
      setShowAddModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this joinee and all their training data?")) return;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.delete(`${API}/admin/joined/${id}`, authHeaders);
      setJoinees(joinees.filter(j => j.id !== id));
      toast.success("Joinee deleted");
    } catch (err) {
      toast.error("Failed to delete joinee");
    }
  };

  const handleViewDetails = async (joinee) => {
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.get(`${API}/admin/joined/${joinee.id}`, authHeaders);
      setSelectedJoinee(res.data);
      setShowViewModal(true);
    } catch (err) {
      toast.error("Failed to load details");
    }
  };

  const handleViewFeedback = async (joinee) => {
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.get(`${API}/admin/joined/${joinee.id}/feedback`, authHeaders);
      setFeedback(res.data);
      setSelectedJoinee(joinee);
      setShowFeedbackModal(true);
    } catch (err) {
      toast.error("Failed to load feedback");
    }
  };

  const handleOpenCredentials = (joinee) => {
    setSelectedJoinee(joinee);
    setCredentialsForm({
      crm_username: joinee.credentials?.crm?.username || "",
      crm_password: joinee.credentials?.crm?.password || "",
      crm_link: joinee.credentials?.crm?.link || "",
      email_address: joinee.credentials?.email?.address || "",
      email_password: joinee.credentials?.email?.password || "",
      email_link: joinee.credentials?.email?.link || ""
    });
    setShowCredentialsModal(true);
  };

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.put(`${API}/admin/joined/${selectedJoinee.id}/credentials`, {
        crm: {
          username: credentialsForm.crm_username,
          password: credentialsForm.crm_password,
          link: credentialsForm.crm_link
        },
        email: {
          address: credentialsForm.email_address,
          password: credentialsForm.email_password,
          link: credentialsForm.email_link
        }
      }, authHeaders);
      toast.success("Credentials saved successfully");
      setShowCredentialsModal(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to save credentials");
    }
  };

  const handleToggleAccess = async (joinee) => {
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.post(`${API}/admin/joined/${joinee.id}/toggle-access`, {}, authHeaders);
      setJoinees(joinees.map(j => 
        j.id === joinee.id ? { ...j, access_enabled: res.data.access_enabled } : j
      ));
      toast.success(res.data.access_enabled ? "Access enabled" : "Access disabled");
    } catch (err) {
      toast.error("Failed to toggle access");
    }
  };

  const handleRegenerateCode = async (joinee) => {
    if (!window.confirm("Generate a new access code? The old code will no longer work.")) return;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.post(`${API}/admin/joined/${joinee.id}/regenerate-code`, {}, authHeaders);
      setJoinees(joinees.map(j => 
        j.id === joinee.id ? { ...j, access_code: res.data.access_code } : j
      ));
      toast.success("New access code generated");
      navigator.clipboard.writeText(res.data.access_code);
      toast.info("Code copied to clipboard");
    } catch (err) {
      toast.error("Failed to regenerate code");
    }
  };

  const handleResendWelcome = async (joinee) => {
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.post(`${API}/admin/joined/${joinee.id}/resend-welcome`, {}, authHeaders);
      toast.success("Welcome email sent!");
    } catch (err) {
      toast.error("Failed to send email");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const getProgressBadge = (completedDays = []) => {
    const count = completedDays.length;
    if (count === 0) return { text: "Not Started", color: "bg-gray-100 text-gray-600" };
    if (count < 4) return { text: `Day ${count}/4`, color: "bg-amber-100 text-amber-700" };
    return { text: "Completed", color: "bg-green-100 text-green-700" };
  };

  // Email template functions
  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.put(`${API}/admin/joined/email-template`, templateForm, authHeaders);
      toast.success("Email template saved successfully!");
      fetchEmailTemplate();
    } catch (err) {
      toast.error("Failed to save email template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async () => {
    if (!window.confirm("Reset the email template to default? Your changes will be lost.")) return;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.post(`${API}/admin/joined/email-template/reset`, {}, authHeaders);
      setEmailTemplate(res.data.template);
      setTemplateForm({
        name: res.data.template.name || "",
        subject: res.data.template.subject || "",
        body: res.data.template.body || ""
      });
      toast.success("Email template reset to default");
    } catch (err) {
      toast.error("Failed to reset email template");
    }
  };

  const handlePreviewTemplate = async () => {
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.post(`${API}/admin/joined/email-template/preview`, templateForm, authHeaders);
      setPreviewHtml(res.data);
      setShowPreview(true);
    } catch (err) {
      toast.error("Failed to generate preview");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="joined-admin">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-playfair text-2xl text-charcoal flex items-center gap-3">
            <GraduationCap className="w-7 h-7 text-gold" />
            Training Portal Management
          </h1>
          <p className="font-dm text-charcoal-muted text-sm mt-1">
            Manage new joinee training and onboarding
          </p>
        </div>
        {activeTab === "joinees" && (
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 bg-gold hover:bg-gold/90 text-white font-dm font-medium py-2.5 px-5 rounded-lg transition-all"
            data-testid="add-joinee-btn"
          >
            <Plus className="w-4 h-4" />
            Add New Joinee
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("joinees")}
          className={`px-4 py-2 font-dm text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "joinees"
              ? "border-gold text-gold"
              : "border-transparent text-charcoal-muted hover:text-charcoal"
          }`}
        >
          <Users className="w-4 h-4 inline-block mr-2" />
          Joinees
        </button>
        <button
          onClick={() => setActiveTab("email-template")}
          className={`px-4 py-2 font-dm text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "email-template"
              ? "border-gold text-gold"
              : "border-transparent text-charcoal-muted hover:text-charcoal"
          }`}
        >
          <Mail className="w-4 h-4 inline-block mr-2" />
          Email Template
        </button>
      </div>

      {activeTab === "joinees" && (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-dm text-2xl font-bold text-charcoal">{stats.total}</p>
                    <p className="font-dm text-xs text-charcoal-muted">Total Joinees</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-dm text-2xl font-bold text-charcoal">{stats.in_progress}</p>
                    <p className="font-dm text-xs text-charcoal-muted">In Progress</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-dm text-2xl font-bold text-charcoal">{stats.completed}</p>
                    <p className="font-dm text-xs text-charcoal-muted">Completed</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-dm text-2xl font-bold text-charcoal">{stats.total_feedback}</p>
                    <p className="font-dm text-xs text-charcoal-muted">Feedback Entries</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
              type="text"
              placeholder="Search by name, email, or position..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
              data-testid="search-joinees"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
          >
            <option value="all">All Status</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Joinees Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left font-dm text-xs text-charcoal-muted uppercase tracking-wider">Joinee</th>
                <th className="px-4 py-3 text-left font-dm text-xs text-charcoal-muted uppercase tracking-wider">Position</th>
                <th className="px-4 py-3 text-left font-dm text-xs text-charcoal-muted uppercase tracking-wider">Access Code</th>
                <th className="px-4 py-3 text-left font-dm text-xs text-charcoal-muted uppercase tracking-wider">Progress</th>
                <th className="px-4 py-3 text-left font-dm text-xs text-charcoal-muted uppercase tracking-wider">Credentials</th>
                <th className="px-4 py-3 text-left font-dm text-xs text-charcoal-muted uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right font-dm text-xs text-charcoal-muted uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredJoinees.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="font-dm text-charcoal-muted">No joinees found</p>
                  </td>
                </tr>
              ) : (
                filteredJoinees.map((joinee) => {
                  const progressBadge = getProgressBadge(joinee.completed_days);
                  return (
                    <tr key={joinee.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-dm font-medium">
                            {joinee.name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-dm font-medium text-charcoal">{joinee.name}</p>
                            <p className="font-dm text-xs text-charcoal-muted">{joinee.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <p className="font-dm text-sm text-charcoal">{joinee.position}</p>
                        {joinee.department && (
                          <p className="font-dm text-xs text-charcoal-muted">{joinee.department}</p>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <code className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{joinee.access_code}</code>
                          <button
                            onClick={() => copyToClipboard(joinee.access_code)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Copy code"
                          >
                            <Copy className="w-3 h-3 text-gray-400" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${progressBadge.color}`}>
                            {progressBadge.text}
                          </span>
                          {joinee.completed_days?.length > 0 && (
                            <button
                              onClick={() => handleViewFeedback(joinee)}
                              className="p-1 hover:bg-gray-100 rounded"
                              title="View feedback"
                            >
                              <MessageSquare className="w-4 h-4 text-purple-500" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => handleOpenCredentials(joinee)}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-dm text-sm"
                        >
                          <Key className="w-4 h-4" />
                          Manage
                        </button>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          joinee.access_enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {joinee.access_enabled ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleViewDetails(joinee)}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-charcoal"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(joinee)}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-charcoal"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleAccess(joinee)}
                            className={`p-2 hover:bg-gray-100 rounded-lg ${joinee.access_enabled ? 'text-amber-500' : 'text-green-500'}`}
                            title={joinee.access_enabled ? 'Disable access' : 'Enable access'}
                          >
                            <Power className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRegenerateCode(joinee)}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-charcoal"
                            title="Regenerate code"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleResendWelcome(joinee)}
                            className="p-2 hover:bg-gray-100 rounded-lg text-blue-500 hover:text-blue-600"
                            title="Resend welcome email"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(joinee.id)}
                            className="p-2 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <h2 className="font-playfair text-xl text-charcoal">
                  {editMode ? 'Edit Joinee' : 'Add New Joinee'}
                </h2>
                <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                      required
                      disabled={editMode}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-dm text-sm text-charcoal mb-1">Position *</label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-dm text-sm text-charcoal mb-1">Department</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Reporting Manager</label>
                  <input
                    type="text"
                    value={formData.reporting_manager}
                    onChange={(e) => setFormData({ ...formData, reporting_manager: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                  />
                </div>
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold resize-none"
                  />
                </div>
                {!editMode && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <p className="font-dm text-sm text-blue-700">
                      <strong>Note:</strong> A welcome email with login credentials will be automatically sent to the joinee.
                    </p>
                  </div>
                )}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gold hover:bg-gold/90 text-white font-dm font-medium py-2.5 rounded-lg flex items-center justify-center gap-2"
                  >
                    {editMode ? 'Save Changes' : 'Add Joinee'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Credentials Modal */}
      <AnimatePresence>
        {showCredentialsModal && selectedJoinee && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <h2 className="font-playfair text-xl text-charcoal">
                  Manage Credentials - {selectedJoinee.name}
                </h2>
                <button onClick={() => setShowCredentialsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <form onSubmit={handleSaveCredentials} className="p-6 space-y-6">
                {/* CRM Credentials */}
                <div>
                  <h3 className="font-dm font-medium text-charcoal mb-3 flex items-center gap-2">
                    <Building className="w-4 h-4 text-blue-500" />
                    CRM Credentials (Unlocked after Day 2)
                  </h3>
                  <div className="space-y-3 bg-blue-50/50 p-4 rounded-lg">
                    <div>
                      <label className="block font-dm text-xs text-charcoal-muted mb-1">Username</label>
                      <input
                        type="text"
                        value={credentialsForm.crm_username}
                        onChange={(e) => setCredentialsForm({ ...credentialsForm, crm_username: e.target.value })}
                        placeholder="CRM username"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                      />
                    </div>
                    <div>
                      <label className="block font-dm text-xs text-charcoal-muted mb-1">Password</label>
                      <input
                        type="text"
                        value={credentialsForm.crm_password}
                        onChange={(e) => setCredentialsForm({ ...credentialsForm, crm_password: e.target.value })}
                        placeholder="CRM password"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                      />
                    </div>
                    <div>
                      <label className="block font-dm text-xs text-charcoal-muted mb-1">CRM Link</label>
                      <input
                        type="url"
                        value={credentialsForm.crm_link}
                        onChange={(e) => setCredentialsForm({ ...credentialsForm, crm_link: e.target.value })}
                        placeholder="https://crm.example.com"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                      />
                    </div>
                  </div>
                </div>

                {/* Email Credentials */}
                <div>
                  <h3 className="font-dm font-medium text-charcoal mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-orange-500" />
                    Email Credentials (Unlocked after Day 3)
                  </h3>
                  <div className="space-y-3 bg-orange-50/50 p-4 rounded-lg">
                    <div>
                      <label className="block font-dm text-xs text-charcoal-muted mb-1">Email Address</label>
                      <input
                        type="email"
                        value={credentialsForm.email_address}
                        onChange={(e) => setCredentialsForm({ ...credentialsForm, email_address: e.target.value })}
                        placeholder="user@creativeinfotech.net"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                      />
                    </div>
                    <div>
                      <label className="block font-dm text-xs text-charcoal-muted mb-1">Password</label>
                      <input
                        type="text"
                        value={credentialsForm.email_password}
                        onChange={(e) => setCredentialsForm({ ...credentialsForm, email_password: e.target.value })}
                        placeholder="Email password"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                      />
                    </div>
                    <div>
                      <label className="block font-dm text-xs text-charcoal-muted mb-1">Email Platform Link</label>
                      <input
                        type="url"
                        value={credentialsForm.email_link}
                        onChange={(e) => setCredentialsForm({ ...credentialsForm, email_link: e.target.value })}
                        placeholder="https://mail.google.com"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCredentialsModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg font-dm text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gold hover:bg-gold/90 text-white font-dm font-medium py-2.5 rounded-lg"
                  >
                    Save Credentials
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Feedback Modal */}
      <AnimatePresence>
        {showFeedbackModal && selectedJoinee && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <h2 className="font-playfair text-xl text-charcoal">
                  Training Feedback - {selectedJoinee.name}
                </h2>
                <button onClick={() => setShowFeedbackModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-6">
                {feedback.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="font-dm text-charcoal-muted">No feedback submitted yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {feedback.map((fb, index) => (
                      <div key={index} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-dm font-medium text-charcoal">Day {fb.day}</span>
                          <span className="font-dm text-xs text-charcoal-muted">
                            {new Date(fb.submitted_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="font-dm text-sm text-charcoal-muted">{fb.feedback}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* View Details Modal */}
      <AnimatePresence>
        {showViewModal && selectedJoinee && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <h2 className="font-playfair text-xl text-charcoal">Joinee Details</h2>
                <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-playfair text-2xl">
                    {selectedJoinee.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-playfair text-xl text-charcoal">{selectedJoinee.name}</h3>
                    <p className="font-dm text-charcoal-muted">{selectedJoinee.email}</p>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="font-dm text-xs text-charcoal-muted mb-1">Position</p>
                    <p className="font-dm text-sm text-charcoal">{selectedJoinee.position}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="font-dm text-xs text-charcoal-muted mb-1">Department</p>
                    <p className="font-dm text-sm text-charcoal">{selectedJoinee.department || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="font-dm text-xs text-charcoal-muted mb-1">Reporting Manager</p>
                    <p className="font-dm text-sm text-charcoal">{selectedJoinee.reporting_manager || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="font-dm text-xs text-charcoal-muted mb-1">Access Code</p>
                    <code className="font-mono text-sm">{selectedJoinee.access_code}</code>
                  </div>
                </div>

                {/* Progress */}
                <div>
                  <p className="font-dm text-sm text-charcoal mb-3">Training Progress</p>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4].map((day) => (
                      <div
                        key={day}
                        className={`flex-1 h-2 rounded-full ${
                          selectedJoinee.completed_days?.includes(day) ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="font-dm text-xs text-charcoal-muted mt-2">
                    {selectedJoinee.completed_days?.length || 0} of 4 days completed
                  </p>
                </div>

                {/* Timestamps */}
                <div className="border-t border-gray-100 pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="font-dm text-sm text-charcoal-muted">Added On</span>
                    <span className="font-dm text-sm text-charcoal">
                      {new Date(selectedJoinee.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {selectedJoinee.last_login && (
                    <div className="flex justify-between">
                      <span className="font-dm text-sm text-charcoal-muted">Last Login</span>
                      <span className="font-dm text-sm text-charcoal">
                        {new Date(selectedJoinee.last_login).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
        </>
      )}

      {/* Email Template Tab */}
      {activeTab === "email-template" && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-playfair text-xl text-charcoal flex items-center gap-2">
                <FileText className="w-5 h-5 text-gold" />
                Welcome Email Template
              </h2>
              <p className="font-dm text-sm text-charcoal-muted mt-1">
                Customize the email sent to new joinees when they are added
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleResetTemplate}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm text-charcoal-muted hover:bg-gray-50 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset to Default
              </button>
              <button
                onClick={handlePreviewTemplate}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg font-dm text-sm text-charcoal-muted hover:bg-gray-50 transition-colors"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
            </div>
          </div>

          <div className="space-y-5">
            {/* Placeholders Info */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <p className="font-dm text-sm text-blue-700 font-medium mb-2">Available Placeholders</p>
              <div className="flex flex-wrap gap-2">
                {['{{joinee_name}}', '{{joinee_email}}', '{{position}}', '{{department}}', '{{access_code}}', '{{portal_url}}'].map((ph) => (
                  <code key={ph} className="px-2 py-1 bg-blue-100 rounded text-xs text-blue-700 font-mono">{ph}</code>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block font-dm text-sm text-charcoal mb-2">Email Subject</label>
              <input
                type="text"
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg font-dm text-sm focus:outline-none focus:border-gold"
                placeholder="Welcome to Creative Infotech - Training Portal Access"
              />
            </div>

            {/* Body */}
            <div>
              <label className="block font-dm text-sm text-charcoal mb-2">Email Body (HTML)</label>
              <textarea
                value={templateForm.body}
                onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                rows={20}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg font-mono text-xs focus:outline-none focus:border-gold resize-y"
                placeholder="Enter HTML content..."
              />
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveTemplate}
                disabled={savingTemplate}
                className="flex items-center gap-2 bg-gold hover:bg-gold/90 text-white font-dm font-medium py-2.5 px-6 rounded-lg transition-all disabled:opacity-50"
              >
                {savingTemplate ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <h2 className="font-playfair text-xl text-charcoal">Email Preview</h2>
                <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4 pb-4 border-b border-gray-100">
                  <p className="font-dm text-sm text-charcoal-muted">Subject:</p>
                  <p className="font-dm text-charcoal font-medium">{previewHtml.subject}</p>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div 
                    className="p-4 bg-gray-50"
                    dangerouslySetInnerHTML={{ __html: previewHtml.body }}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default JoinedPortalAdmin;
